import React, { useState, useEffect } from 'react';
import { Key, Save, Check, AlertCircle, Info, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from '../hooks/use-toast';
import { defaultSettings, presentationTemplates } from '../data/mockData';

const Settings = () => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('slidegenius_settings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      localStorage.setItem('slidegenius_settings', JSON.stringify(settings));
      setIsSaving(false);
      toast({
        title: 'Configuración guardada',
        description: 'Tus preferencias han sido actualizadas correctamente.'
      });
    }, 500);
  };

  const testConnection = () => {
    const currentKey = settings.apiProvider === 'emergent' ? settings.emergentKey : settings.claudeKey;
    if (!currentKey) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa una API key antes de probar la conexión.',
        variant: 'destructive'
      });
      return;
    }

    setTestingConnection(true);
    setTimeout(() => {
      setTestingConnection(false);
      toast({
        title: 'Conexión exitosa',
        description: `La API key de ${settings.apiProvider === 'emergent' ? 'Emergent' : 'Claude'} es válida.`
      });
    }, 1500);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Configuración</h1>
        <p className="text-gray-500 mt-1">Administra tus preferencias y claves de API</p>
      </div>

      {/* API Configuration */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Key className="w-5 h-5 text-teal-600" />
            Configuración de API
          </CardTitle>
          <CardDescription>
            Selecciona y configura el proveedor de IA para la generación de presentaciones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Provider Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Proveedor de IA</Label>
            <RadioGroup
              value={settings.apiProvider}
              onValueChange={(value) => setSettings({ ...settings, apiProvider: value })}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className={`relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${settings.apiProvider === 'claude'
                  ? 'border-teal-500 bg-teal-50/50'
                  : 'border-gray-200 hover:border-gray-300'
                }`}>
                <RadioGroupItem value="claude" id="claude" className="mt-1" />
                <Label htmlFor="claude" className="ml-3 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Claude API Key</span>
                    <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded-full">Recomendado</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Usar directamente tu key de Anthropic Claude
                  </p>
                </Label>
              </div>

              <div className={`relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${settings.apiProvider === 'emergent'
                  ? 'border-teal-500 bg-teal-50/50'
                  : 'border-gray-200 hover:border-gray-300'
                }`}>
                <RadioGroupItem value="emergent" id="emergent" className="mt-1" />
                <Label htmlFor="emergent" className="ml-3 cursor-pointer">
                  <span className="font-medium text-gray-900">Emergent LLM Key</span>
                  <p className="text-sm text-gray-500 mt-1">
                    Key universal compatible con Claude, OpenAI y Gemini
                  </p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* API Key Input */}
          {settings.apiProvider === 'emergent' ? (
            <div className="space-y-2">
              <Label htmlFor="emergent-key" className="text-sm font-medium">Emergent LLM Key</Label>
              <div className="flex gap-2">
                <Input
                  id="emergent-key"
                  type="password"
                  placeholder="ek_xxxxxxxxxxxxxxxxxxxxxxxx"
                  value={settings.emergentKey}
                  onChange={(e) => setSettings({ ...settings, emergentKey: e.target.value })}
                  className="flex-1 border-gray-300 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="shrink-0"
                >
                  {testingConnection ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    'Probar'
                  )}
                </Button>
              </div>
              <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 rounded-lg">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-700">
                  Puedes obtener tu Emergent LLM Key desde <span className="font-medium">Perfil → Universal Key</span>.
                  Esta key funciona con múltiples proveedores de IA.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="claude-key" className="text-sm font-medium">Claude API Key</Label>
              <div className="flex gap-2">
                <Input
                  id="claude-key"
                  type="password"
                  placeholder="sk-ant-xxxxxxxxxxxxxxxxxxxxxxxx"
                  value={settings.claudeKey}
                  onChange={(e) => setSettings({ ...settings, claudeKey: e.target.value })}
                  className="flex-1 border-gray-300 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="shrink-0"
                >
                  {testingConnection ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    'Probar'
                  )}
                </Button>
              </div>
              <div className="flex items-start gap-2 mt-2 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700">
                  Obtén tu API key desde <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">console.anthropic.com</a>.
                  Tu key se guarda localmente y nunca se comparte.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generation Preferences */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-teal-600" />
            Preferencias de Generación
          </CardTitle>
          <CardDescription>
            Configura los valores predeterminados para las nuevas presentaciones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Plantilla por defecto</Label>
              <Select
                value={settings.defaultTemplate}
                onValueChange={(value) => setSettings({ ...settings, defaultTemplate: value })}
              >
                <SelectTrigger className="border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presentationTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: template.color }}
                        />
                        {template.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Máximo de slides</Label>
              <Select
                value={String(settings.maxSlides)}
                onValueChange={(value) => setSettings({ ...settings, maxSlides: parseInt(value) })}
              >
                <SelectTrigger className="border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 slides</SelectItem>
                  <SelectItem value="8">8 slides</SelectItem>
                  <SelectItem value="10">10 slides</SelectItem>
                  <SelectItem value="12">12 slides</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Idioma de contenido</Label>
              <Select
                value={settings.language}
                onValueChange={(value) => setSettings({ ...settings, language: value })}
              >
                <SelectTrigger className="border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="pt">Português</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-teal-600 hover:bg-teal-700 text-white px-8"
        >
          {isSaving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Configuración
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
